import {t} from "../i18n/index.js";

// Pure math for the three "Målinger" tools (Mål afstand/højde/areal) -
// deliberately separate from any THREE.js/DOM-dependent visual code (same
// split this codebase already uses for pipeAnalysis.js/PipeVisual.js),
// specifically so this math stays Level 1 (no-DOM) testable under plain
// Vitest - constructing an actual FeatureVisual/label needs `document`,
// which this project's Vitest run does not provide (see this project's own
// PipeVisual.js/HoverTooltip.js audit notes).
//
// Points throughout are plain [x,y,z] arrays (this module's own convention,
// matching FeatureVisual.rawCoordinates()'s own shape) - never a THREE
// object, so callers never need THREE imported just to call this math.

export function distanceBetween(a, b){
	return Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2]);
}

export function totalPolylineLength(points){
	let total = 0;
	for(let i = 1; i < points.length; i++) total += distanceBetween(points[i - 1], points[i]);
	return total;
}

// The point on the polyline itself at `fraction` (0-1) of the TOTAL path
// length walked along the actual segments - not a straight-line average of
// the endpoints. User-requested fix: Mål afstand's summary label used to
// anchor at the last captured point; "midt på den målte linje" (the middle
// of the measured LINE) means the geometric midpoint of the path as drawn,
// which for a multi-segment line is not the same point a naive
// first/last-endpoint average would give.
export function pointAlongPolyline(points, fraction){
	if(points.length === 0) return null;
	if(points.length === 1) return points[0];

	const total = totalPolylineLength(points);
	if(total <= 1e-12) return points[0];

	const target = total * fraction;
	let accumulated = 0;
	for(let i = 1; i < points.length; i++){
		const a = points[i - 1], b = points[i];
		const segmentLength = distanceBetween(a, b);
		if(accumulated + segmentLength >= target || i === points.length - 1){
			const remaining = target - accumulated;
			const t = segmentLength > 1e-12 ? Math.min(1, remaining / segmentLength) : 0;
			return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
		}
		accumulated += segmentLength;
	}
	return points[points.length - 1];
}

// Unit vector perpendicular to (dx,dy,dz), used to push a result label off
// to the side of the line it summarises rather than sitting exactly on top
// of it. Reuses the exact same deterministic fallback chain as
// geometry/arcFromSagitta.js's determineArcPlane() (world-Z-perpendicular
// first - "beside" a normally near-horizontal captured line/pipe/edge -
// then a horizontal fallback for the rare near-vertical segment) for the
// same reason: never camera-dependent, always well-defined, same input
// always gives the same side.
function perpendicularDirection(dx, dy, dz){
	const len = Math.hypot(dx, dy, dz);
	if(len < 1e-12) return [1, 0, 0];
	const ux = dx / len, uy = dy / len, uz = dz / len;

	const upDot = uz; // dot(u, world +Z)
	let px = -upDot * ux, py = -upDot * uy, pz = 1 - upDot * uz;
	let plen = Math.hypot(px, py, pz);
	if(plen > 1e-6) return [px / plen, py / plen, pz / plen];

	// near-vertical segment - world Z gave no usable perpendicular signal
	const rx = Math.abs(ux) < 0.9 ? 1 : 0, ry = Math.abs(ux) < 0.9 ? 0 : 1;
	const rdot = rx * ux + ry * uy;
	px = rx - rdot * ux; py = ry - rdot * uy; pz = -rdot * uz;
	plen = Math.hypot(px, py, pz);
	return plen > 1e-9 ? [px / plen, py / plen, pz / plen] : [1, 0, 0];
}

// Same station-based point as pointAlongPolyline(), but pushed sideways by
// `offsetDistance` (perpendicular to the LOCAL segment direction at that
// station) - user-requested fix: a result label anchored exactly ON the
// line/path can sit directly on top of it (and, for a line's own last
// segment, on top of the last vertex's own coordinate label too). The
// offset direction is always well-defined (see perpendicularDirection()
// above), so this never degenerates for a straight or near-vertical
// segment.
export function offsetPointAlongPolyline(points, fraction, offsetDistance){
	if(points.length === 0) return null;
	if(points.length === 1) return points[0];

	const total = totalPolylineLength(points);
	if(total <= 1e-12) return points[0];

	const target = total * fraction;
	let accumulated = 0;
	for(let i = 1; i < points.length; i++){
		const a = points[i - 1], b = points[i];
		const segmentLength = distanceBetween(a, b);
		if(accumulated + segmentLength >= target || i === points.length - 1){
			const remaining = target - accumulated;
			const t = segmentLength > 1e-12 ? Math.min(1, remaining / segmentLength) : 0;
			const mx = a[0] + (b[0] - a[0]) * t, my = a[1] + (b[1] - a[1]) * t, mz = a[2] + (b[2] - a[2]) * t;
			const [px, py, pz] = perpendicularDirection(b[0] - a[0], b[1] - a[1], b[2] - a[2]);
			return [mx + px * offsetDistance, my + py * offsetDistance, mz + pz * offsetDistance];
		}
		accumulated += segmentLength;
	}
	const last = points[points.length - 1];
	return [last[0], last[1], last[2]];
}

// The offset distance itself: a modest fraction of the line's own total
// length (so it scales naturally with the feature's own size - a long
// captured pipe gets a further-offset label than a short one), floored at a
// small absolute minimum so a very short line's label still clears the line
// by a visible margin.
export function labelOffsetDistance(totalLength){
	return Math.max(totalLength * 0.06, 0.15);
}

// XY-plane shoelace area (footprint area, not 3D surface area) - the same
// convention Potree's own native Measure.js area tool uses, and the same
// convention this project's own "Areal og volumen" referenceArea already
// established (geometry/volume/polygon2D.js's signedArea) - kept as its
// own tiny local copy rather than importing from the volume module, since
// that module's own validation/degenerate-handling machinery is specific
// to the volume pipeline's needs, not this simple measurement tool's.
export function polygonArea(points){
	let area = 0;
	const n = points.length;
	for(let i = 0; i < n; i++){
		const [x1, y1] = points[i];
		const [x2, y2] = points[(i + 1) % n];
		area += x1 * y2 - x2 * y1;
	}
	return Math.abs(area) / 2;
}

export function polygonPerimeter(points){
	let total = 0;
	const n = points.length;
	for(let i = 0; i < n; i++) total += distanceBetween(points[i], points[(i + 1) % n]);
	return total;
}

// Height-measurement auxiliary right triangle for exactly 2 points (a, b -
// order doesn't matter, this always sorts by z first). Matches Potree's own
// native height-measurement geometry (src/utils/Measure.js's height-update
// logic): the auxiliary corner sits at the HIGHER point's own (x,y) but at
// the LOWER point's z, so edge low->corner is horizontal, edge
// corner->high is vertical, and the right angle (90 deg) is always at the
// corner - angleAtLow/angleAtHigh are the OTHER two corners of that right
// triangle, which necessarily sum to exactly 90 deg ("de to vinkler der
// ikke er 90" - the two angles that are not the right angle).
export function computeHeightTriangle(a, b){
	const [low, high] = a[2] <= b[2] ? [a, b] : [b, a];
	const corner = [high[0], high[1], low[2]];

	const horizontalDistance = distanceBetween(low, corner);
	const verticalDistance = distanceBetween(corner, high);
	const directDistance = distanceBetween(low, high);

	const angleAtLow = (horizontalDistance + verticalDistance) > 1e-9
		? Math.atan2(verticalDistance, horizontalDistance) * (180 / Math.PI)
		: 0;
	const angleAtHigh = 90 - angleAtLow;

	return {low, high, corner, horizontalDistance, verticalDistance, directDistance, angleAtLow, angleAtHigh};
}

// Dynamic promille/procent slope label for Mål højde's result label (user-
// requested, replacing a fixed "Hældning: X°" degree readout): below 10%
// grade, promille (‰) gives a more natural, whole-ish number for a gentle
// slope (matches standard ledningsdrift/vejprofil practice - the same
// "promille for gentle grades" convention geometry/zProfile.js's own Sæt
// fald/hældning dialog already uses, just chosen AUTOMATICALLY here from
// the measured slope's own magnitude instead of a manual unit picker); at
// or above 10%, percent keeps the number from ballooning into the hundreds/
// thousands of ‰. The label's own NAME changes with the unit (task: "husk
// at ændre navnet fra 'Hældning' til hhv. Promillehældning og
// Procenthældning"), so a bare number is never ambiguous about its unit.
// Returns the full "Navn: værdi" string (not just the number) since the
// name itself is part of what changes.
export const SLOPE_PROMILLE_THRESHOLD = 0.10; // 10% = 100‰ - the switchover point

export function formatSlopeLabel(verticalDistance, horizontalDistance){
	if(horizontalDistance < 1e-9) return t("Procenthældning: lodret");
	const ratio = verticalDistance / horizontalDistance;
	if(ratio < SLOPE_PROMILLE_THRESHOLD) return t("Promillehældning: {0}‰", (ratio * 1000).toFixed(0));
	return t("Procenthældning: {0}%", (ratio * 100).toFixed(1));
}

// User-requested dynamic label placement for Mål højde's result label: for
// a small triangle, an "extend past the high point" anchor is needed to
// stay clear of the auxiliary triangle's own two edge-midpoint labels - but
// for a large enough triangle, sitting inside its own centroid reads more
// naturally than floating off to the side. "Large enough" is judged from
// the triangle's own two legs (world-space, camera-independent - the same
// kind of static, always-well-defined heuristic this module already uses
// for every other label-placement decision) - BOTH legs need to clear this
// floor before the centroid placement is used, so a long-but-thin triangle
// (one tiny leg) still falls back to the corner-extension placement, where
// the edge labels would otherwise be cramped onto that tiny leg anyway.
export const CENTROID_PLACEMENT_MIN_LEG_M = 1.5;

export function heightSummaryLabelAnchor(t){
	if(t.horizontalDistance >= CENTROID_PLACEMENT_MIN_LEG_M && t.verticalDistance >= CENTROID_PLACEMENT_MIN_LEG_M){
		return [
			(t.low[0] + t.high[0] + t.corner[0]) / 3,
			(t.low[1] + t.high[1] + t.corner[1]) / 3,
			(t.low[2] + t.high[2] + t.corner[2]) / 3,
		];
	}
	// Small triangle: extend past the "high" point along the low->high
	// direction, rather than sitting at the corner - found via live testing:
	// for a shallow/short triangle, the corner sits close enough to the
	// auxiliary vertical edge's own midpoint label that the two visually
	// overlapped and became unreadable. Extending along the segment's own
	// direction stays clear of the corner AND both edge-midpoint labels for
	// any non-degenerate triangle.
	const dx = t.high[0] - t.low[0], dy = t.high[1] - t.low[1], dz = t.high[2] - t.low[2];
	return [t.high[0] + dx * 0.25, t.high[1] + dy * 0.25, t.high[2] + dz * 0.25];
}

// ── Mål vinkel / Mål cirkel / Mål azimut ─────────────────────────────────
// Ren vektormatematik (plain [x,y,z]-arrays, ingen THREE) for de tre
// målinger kopieret fra de gamle native "Mål"-værktøjer og genskrevet til
// Digitaliserings eget format - se tools/angleMeasureTool.js,
// circleMeasureTool.js og azimuthTool.js.

const sub3 = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const dot3 = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross3 = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const norm3 = (a) => Math.hypot(a[0], a[1], a[2]);
const scale3 = (a, s) => [a[0] * s, a[1] * s, a[2] * s];
const add3 = (a, b) => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const RAD_TO_DEG = 180 / Math.PI;

// Vinklen (grader, 0-180) i `vertex` mellem benene vertex->a og vertex->b,
// i 3D. atan2(|kryds|, prik) i stedet for acos(): numerisk stabil helt ud
// mod 0 og 180 grader. null hvis et ben har længde 0 (vinklen er udefineret).
export function angleAtVertex(vertex, a, b){
	const v1 = sub3(a, vertex), v2 = sub3(b, vertex);
	if(norm3(v1) < 1e-9 || norm3(v2) < 1e-9) return null;
	return Math.atan2(norm3(cross3(v1, v2)), dot3(v1, v2)) * RAD_TO_DEG;
}

// Enhedsvektor midt i vinklen (halveringslinjen). null ved en (næsten) lige
// vinkel (180 grader), hvor retningen er tvetydig, eller et nul-langt ben.
export function angleBisector(vertex, a, b){
	const v1 = sub3(a, vertex), v2 = sub3(b, vertex);
	const l1 = norm3(v1), l2 = norm3(v2);
	if(l1 < 1e-9 || l2 < 1e-9) return null;
	const sum = add3(scale3(v1, 1 / l1), scale3(v2, 1 / l2));
	const len = norm3(sum);
	return len < 1e-6 ? null : scale3(sum, 1 / len);
}

// Buen (radius `radius`) i vinklens plan fra ben a til ben b - sfærisk
// interpolation mellem de to enhedsretninger. null når vinklen er 0 eller
// 180 grader (ingen veldefineret plan/bue).
export function angleArcPoints(vertex, a, b, radius, segments = 24){
	const v1 = sub3(a, vertex), v2 = sub3(b, vertex);
	const l1 = norm3(v1), l2 = norm3(v2);
	if(l1 < 1e-9 || l2 < 1e-9) return null;
	const u1 = scale3(v1, 1 / l1), u2 = scale3(v2, 1 / l2);
	const theta = Math.atan2(norm3(cross3(u1, u2)), dot3(u1, u2));
	if(theta < 1e-6 || Math.PI - theta < 1e-6) return null;
	const s = Math.sin(theta);
	const points = [];
	for(let i = 0; i <= segments; i++){
		const t = i / segments;
		const dir = add3(scale3(u1, Math.sin((1 - t) * theta) / s), scale3(u2, Math.sin(t * theta) / s));
		points.push(add3(vertex, scale3(dir, radius)));
	}
	return points;
}

// Cirklen gennem tre punkter i 3D (omskreven cirkel). Returnerer
// {center, radius, normal, u, v} - normal er enhedsnormalen til planen
// gennem de tre punkter, u/v en ortonormal basis i planen (u peger mod a)
// - eller null, hvis punkterne er (næsten) kollineære. Samme cirkel som
// native Potree.Utils.computeCircleCenter(), men i ren matematik og med
// et eksplicit kollinear-tjek.
export function circumcircle(a, b, c){
	const ab = sub3(b, a), ac = sub3(c, a);
	const n = cross3(ab, ac);
	const nLen = norm3(n);
	if(nLen < 1e-9 * Math.max(norm3(ab) * norm3(ac), 1e-12)) return null;

	// center = a + ((|ac|^2 * ab - |ab|^2 * ac) x n) / ... , se test for
	// verifikation (lige afstand til a/b/c, ligger i planen).
	const ab2 = dot3(ab, ab), ac2 = dot3(ac, ac);
	const offset = scale3(cross3(sub3(scale3(ac, ab2), scale3(ab, ac2)), n), 1 / (2 * nLen * nLen));
	const center = add3(a, offset);
	const radius = norm3(sub3(a, center));
	const normal = scale3(n, 1 / nLen);
	const u = scale3(sub3(a, center), 1 / radius);
	const v = cross3(normal, u);
	return {center, radius, normal, u, v};
}

// Lukket ring (første punkt gentages til sidst) af punkter på cirklen.
export function circleRingPoints(circle, segments = 64){
	const points = [];
	for(let i = 0; i <= segments; i++){
		const angle = (2 * Math.PI * i) / segments;
		const c = Math.cos(angle) * circle.radius, s = Math.sin(angle) * circle.radius;
		points.push(add3(circle.center, add3(scale3(circle.u, c), scale3(circle.v, s))));
	}
	return points;
}

export function circleMetrics(circle){
	return {
		radius: circle.radius,
		diameter: 2 * circle.radius,
		circumference: 2 * Math.PI * circle.radius,
		area: Math.PI * circle.radius * circle.radius,
	};
}

// Azimut (grader, 0-360, med uret fra nettets nord = +Y) fra a til b, udelukkende
// ud fra koordinaterne (retningen i XY-planen). Bevidst NETS nord (UTM-nord),
// ikke geodætisk nord og ingen projektion: den gamle native Potree.Utils.
// computeAzimuth() regnede atan2 direkte på længde-/breddegrad-forskelle
// uden cos(bredde) og gav ved Aarhus fx 47 grader for et net-azimut på 30
// (se arbejdsloggen) - og brugte kun den FØRSTE punktskys projektion, hvilket
// ikke passer med modelbaseret CRS. null hvis a og b har samme XY.
export function gridAzimuth(a, b){
	const dx = b[0] - a[0], dy = b[1] - a[1];
	if(Math.hypot(dx, dy) < 1e-9) return null;
	const deg = Math.atan2(dx, dy) * RAD_TO_DEG;
	return deg < 0 ? deg + 360 : deg;
}

export function horizontalDistance(a, b){
	return Math.hypot(b[0] - a[0], b[1] - a[1]);
}

// Vandret bue (i a's kote) fra nord (0 grader) med uret til `azimuthDeg`.
export function azimuthArcPoints(origin, azimuthDeg, radius, segments = 32){
	const points = [];
	for(let i = 0; i <= segments; i++){
		const ang = (azimuthDeg * i / segments) / RAD_TO_DEG;
		points.push([origin[0] + radius * Math.sin(ang), origin[1] + radius * Math.cos(ang), origin[2]]);
	}
	return points;
}
